import React, { Suspense, lazy } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import PinGate from "@/auth/PinGate";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Login from "@/pages/Login";
import { PrivacyPolicy, TermsAndConditions, RefundPolicy, Contact } from "@/pages/Legal";
import NotFound from "@/pages/NotFound";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Cases = lazy(() => import("@/pages/Cases"));
const CaseDetail = lazy(() => import("@/pages/CaseDetail"));
const ImportCases = lazy(() => import("@/pages/ImportCases"));
const FirmSettings = lazy(() => import("@/pages/FirmSettings"));
const QuickBilling = lazy(() => import("@/pages/QuickBilling"));
const QuickQuote = lazy(() => import("@/pages/QuickQuote"));
const QuotationsReceipts = lazy(() => import("@/pages/QuotationsReceipts"));
const BillingHistory = lazy(() => import("@/pages/BillingHistory"));
const Clients = lazy(() => import("@/pages/Clients"));
const Advocates = lazy(() => import("@/pages/Advocates"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const Reminders = lazy(() => import("@/pages/Reminders"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const ECourtsSearch = lazy(() => import("@/pages/ECourtsSearch"));
const Documents = lazy(() => import("@/pages/Documents"));
const Statement = lazy(() => import("@/pages/Statement"));
const DailyBoard = lazy(() => import("@/pages/DailyBoard"));
const Admin = lazy(() => import("@/pages/Admin"));
const Guide = lazy(() => import("@/pages/Guide"));
const Limitation = lazy(() => import("@/pages/Limitation"));
const TimeTracking = lazy(() => import("@/pages/TimeTracking"));
const Team = lazy(() => import("@/pages/Team"));
const Write = lazy(() => import("@/pages/Write"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Support = lazy(() => import("@/pages/Support"));
const JoinFirm = lazy(() => import("@/pages/JoinFirm"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const AiDraft = lazy(() => import("@/pages/AiDraft"));
const CaseStatus = lazy(() => import("@/pages/CaseStatus"));

function AppLoading() {
  return (
    <div className="min-h-[100dvh] flex items-center justify-center heritage-grad" role="status" aria-live="polite">
      <div className="font-serif text-2xl text-oxblood-600">Loading…</div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading, pinLocked } = useAuth();
  if (loading) return <AppLoading />;
  if (pinLocked) return <PinGate />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading, pinLocked } = useAuth();
  if (loading) return <AppLoading />;
  if (pinLocked) return <PinGate />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <>
          <Suspense fallback={<AppLoading />}>
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/join/:token" element={<JoinFirm />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ForgotPassword />} />
            {/* Public on purpose: this is the link an advocate sends a client. */}
            <Route path="/status/:id" element={<CaseStatus />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/cases" element={<Protected><Cases /></Protected>} />
            {/* Before /cases/:id, or "import" is read as a case id. */}
            <Route path="/cases/import" element={<Protected><ImportCases /></Protected>} />
            <Route path="/cases/:id" element={<Protected><CaseDetail /></Protected>} />
            <Route path="/clients" element={<Protected><Clients /></Protected>} />
            <Route path="/advocates" element={<Protected><Advocates /></Protected>} />
            <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
            <Route path="/reminders" element={<Protected><Reminders /></Protected>} />
            <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
            <Route path="/ecourts" element={<Protected><ECourtsSearch /></Protected>} />
            <Route path="/quick-billing" element={<Protected><QuickBilling /></Protected>} />
            <Route path="/quick-quote" element={<Protected><QuickQuote /></Protected>} />
            <Route path="/quick-quote/:id" element={<Protected><QuickQuote /></Protected>} />
            <Route path="/quotations-receipts" element={<Protected><QuotationsReceipts /></Protected>} />
            <Route path="/billing" element={<Protected><BillingHistory /></Protected>} />
            <Route path="/documents" element={<Protected><Documents /></Protected>} />
            <Route path="/time" element={<Protected><TimeTracking /></Protected>} />
            <Route path="/team" element={<Protected><Team /></Protected>} />
            <Route path="/write" element={<Protected><Write /></Protected>} />
            <Route path="/write/:id" element={<Protected><Write /></Protected>} />
            <Route path="/plans" element={<Protected><Pricing /></Protected>} />
            <Route path="/help" element={<Protected><Support /></Protected>} />
            <Route path="/statement" element={<Protected><Statement /></Protected>} />
            <Route path="/daily-board" element={<Protected><DailyBoard /></Protected>} />
            <Route path="/settings" element={<Protected><FirmSettings /></Protected>} />
            <Route path="/limitation" element={<Protected><Limitation /></Protected>} />
            <Route path="/ai-draft" element={<Protected><AiDraft /></Protected>} />
            <Route path="/guide" element={<Protected><Guide /></Protected>} />
            <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
            <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </>
      </AuthProvider>
    </BrowserRouter>
  );
}
